/**
 * Structural sharing between successive Edit snapshots.
 *
 * Materializing the doc after a remote update rebuilds every object, which
 * would hand React a timeline where every clip changed identity — and starve
 * the reconciler's identity fast-path, turning every later local edit into a
 * full-document diff. `shareEdit` walks the fresh snapshot and returns the
 * previous snapshot's objects wherever the content is deep-equal, so identity
 * changes track actual changes. Tracks and clips are matched by id, not array
 * index: a moved clip re-sorts the array, and index matching would lose
 * identity for the whole shifted range.
 *
 * Both snapshots must be treated as immutable by all callers — sharing is
 * only sound because nothing mutates a published snapshot (the same contract
 * materialize.ts relies on).
 */
import type { AnyClip, Edit, Track } from '../format/types';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Return `prev` wherever `next` is deep-equal to it; otherwise `next` with shared children. */
function shareDeep<T>(prev: unknown, next: T): T {
  if (Object.is(prev, next)) return prev as T;
  if (Array.isArray(prev) && Array.isArray(next)) {
    const out: unknown[] = new Array(next.length);
    let same = prev.length === next.length;
    for (let i = 0; i < next.length; i++) {
      out[i] = shareDeep(prev[i], next[i]);
      if (out[i] !== prev[i]) same = false;
    }
    return (same ? prev : out) as T;
  }
  if (isPlainObject(prev) && isPlainObject(next)) {
    const keys = Object.keys(next);
    const out: Record<string, unknown> = {};
    let same = Object.keys(prev).length === keys.length;
    for (const key of keys) {
      out[key] = shareDeep(prev[key], next[key]);
      if (out[key] !== prev[key]) same = false;
    }
    return (same ? prev : out) as T;
  }
  return next;
}

function shareClips(prev: AnyClip[], next: AnyClip[]): AnyClip[] {
  const prevById = new Map(prev.map((clip) => [clip.id, clip]));
  const out = next.map((clip) => shareDeep(prevById.get(clip.id), clip));
  const same = prev.length === next.length && out.every((clip, i) => clip === prev[i]);
  return same ? prev : out;
}

function shareTracks(prev: Track[], next: Track[]): Track[] {
  const prevById = new Map(prev.map((track) => [track.id, track]));
  const out = next.map((track) => {
    const p = prevById.get(track.id);
    if (!p) return track;
    const clips = shareClips(p.clips, track.clips);
    // The cast re-asserts what the spread loses: clips came from this same
    // track, so the kind/clips correlation of the union still holds.
    const merged = clips === track.clips ? track : ({ ...track, clips } as Track);
    return shareDeep(p, merged);
  });
  const same = prev.length === next.length && out.every((track, i) => track === prev[i]);
  return same ? prev : out;
}

export function shareEdit(prev: Edit, next: Edit): Edit {
  if (prev === next) return prev;
  const settings = shareDeep(prev.settings, next.settings);
  const sources = shareDeep(prev.sources, next.sources);
  const tracks = shareTracks(prev.tracks, next.tracks);
  if (
    settings === prev.settings &&
    sources === prev.sources &&
    tracks === prev.tracks &&
    prev.version === next.version
  ) {
    return prev;
  }
  return { ...next, settings, sources, tracks };
}
